import { logbookNavbar, useHasPermission } from "@dexteel/mesf-core";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Button, Menu, MenuItem, Typography } from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { routeClips, routeClipView } from "./routes/clips.routes";
// import { routeCurrentStatus } from "./routes/current-status.routes";
import {
	routeDelaysManager,
	routeDelaysPareto,
	routeDelaysReport,
} from "./routes/delay.routes";
import { routeGantt } from "./routes/gantt.routes";
import {
	routeOverviewMill,
	routeOverviewSawEast,
	routeOverviewWeld165,
} from "./routes/overviews.routes";
import { routePrinters } from "./routes/printers.routes";
import { PRINTERS_PERMISSION } from "../pages/printers/PrintersPage";
import { routeProducts } from "./routes/products.routes";
import {
	routePiecesLengthReport,
	routeBundlesLengthReport,
} from "./routes/reports.routes";
import { routeWeldlineSetup } from "./routes/weldline-setup.routes";
import { routeSawQualityChecksReport } from "./routes/saw-quality-checks.routes";
import {
	routeWeldTest,
	routeWeldTestsManager,
} from "./routes/weld-test.routes";
import { routeCalendar, routeDayDisplay } from "./routes/calendar.routes";
import { routeWorkOrders } from "./routes/work-orders.routes";
import { routeSawTablet } from "./routes/saw-tablet.routes";
import { routeTagStation } from "./routes/tag-station.routes";
import { TAG_STATION_ASSET_NAME } from "../pages/tag-station/tagStation";
import {
	SAW_TABLET_ASSET_NAME,
	SAW_TABLET_PERMISSION,
} from "../pages/saw-tablet/sawTabletConfig";
import { WELD_AREA_NAME } from "../pages/home/hooks/useWeldLines";
import { WELD165_NAME } from "../shared/colors";
import { type UserScope, useUserScope } from "./scope";

interface MenuItemConfig {
	label: string;
	to: string;
	permission?: string;
	// Reduced menu: any scope, area defaults only, or the asset the page belongs to
	scope?: "any" | "area" | { asset: string };
	scopeLabel?: string;
}

interface MenuConfig extends Omit<MenuItemConfig, "to"> {
	id: string;
	to?: string;
	items: MenuItemConfig[];
}

const menuConfigData: MenuConfig[] = [
	{
		id: "home",
		label: "Home",
		to: routeGantt,
		items: [],
		scope: "area",
	},
	{
		id: "overviews",
		label: "Overviews",
		items: [
			{
				label: "Weld 165",
				to: routeOverviewWeld165,
				scope: { asset: WELD165_NAME },
				scopeLabel: "Weld 165 Overview",
			},
			{
				label: "20th Street Saw",
				to: routeOverviewSawEast,
				scope: { asset: SAW_TABLET_ASSET_NAME },
				scopeLabel: "20th Street Saw Overview",
			},
			{
				label: "2 Mill",
				to: routeOverviewMill,
				permission: "ui.navbar.mill.overview",
			},
		],
	},
	{
		id: "delays",
		label: "Delays",
		items: [
			{
				label: "Manager",
				to: `${routeDelaysManager}`,
				scope: "any",
				scopeLabel: "Delays Manager",
			},
			{
				label: "Report",
				to: routeDelaysReport,
			},
			{
				label: "Pareto",
				to: routeDelaysPareto,
			},
		],
	},
	{
		id: "products",
		label: "Products",
		items: [
			{
				label: "Products Catalog",
				to: routeProducts,
			},
			{
				label: "Clips Catalog",
				to: routeClips,
			},
		],
	},
	{
		id: "reports",
		label: "Reports",
		items: [
			{
				label: "Bundles Length Report",
				to: routeBundlesLengthReport,
			},
			{
				label: "Pieces Length Report",
				to: routePiecesLengthReport,
			},
		],
	},
	{
		id: "setup",
		label: "Setup",
		items: [
			{
				label: "Weldline 165",
				to: routeWeldlineSetup,
			},
			{
				label: "Clips",
				to: routeClipView,
			},
			{
				label: "Printers",
				to: routePrinters,
				permission: PRINTERS_PERMISSION,
			},
		],
	},
	{
		id: "tests",
		label: "Tests",
		items: [
			{
				label: "Weldline Test",
				to: routeWeldTest,
				scope: { asset: WELD_AREA_NAME },
			},
			{
				label: "Weldline Tests Manager",
				to: routeWeldTestsManager,
			},
			{
				label: "Saw Tablet",
				to: routeSawTablet,
				permission: SAW_TABLET_PERMISSION,
				scope: { asset: SAW_TABLET_ASSET_NAME },
			},
			{
				label: "Saw Quality Checks Report",
				to: routeSawQualityChecksReport,
			},
		],
	},
	{
		id: "work-orders",
		label: "Work Orders",
		to: routeWorkOrders,
		items: [],
	},
	{
		id: "calendar",
		label: "Calendar",
		items: [
			{
				label: "Month Planner",
				to: routeCalendar,
			},
			{
				label: "Day Display",
				to: routeDayDisplay,
			},
		],
	},
	{
		id: "tag-station",
		label: "Tag Station",
		to: routeTagStation,
		items: [],
		scope: { asset: TAG_STATION_ASSET_NAME },
	},
	// {
	// 	id: "current-status",
	// 	label: "Current Status",
	// 	to: routeCurrentStatus,
	// 	items: [],
	// },
];

const allItems: MenuItemConfig[] = menuConfigData.flatMap((menu) =>
	menu.to ? [{ ...menu, to: menu.to }] : menu.items,
);

const assetOf = (item: MenuItemConfig) =>
	typeof item.scope === "object" ? item.scope.asset : null;

const getScopedItems = (scope: UserScope) =>
	allItems.filter((item) => {
		// Any line or area; the Tag Station has no delays
		if (item.scope === "any")
			return scope.defaultAsset?.AssetName !== TAG_STATION_ASSET_NAME;
		if (item.scope === "area") return scope.isArea;
		const asset = assetOf(item);
		return !!asset && scope.covers(asset);
	});

// Page owning a path = the longest menu link it sits under
export const isPathInScope = (pathname: string, scope: UserScope) => {
	const owner = allItems
		.filter(
			(item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
		)
		.sort((a, b) => b.to.length - a.to.length)[0];
	return !!owner && getScopedItems(scope).includes(owner);
};

// Area: its Gantt. Line: its own page, else its area's page, else the first one
export const getScopedHome = (scope: UserScope) => {
	const items = getScopedItems(scope);
	const home =
		items.find((item) => item.scope === "area") ??
		items.find((item) => assetOf(item) === scope.defaultAsset?.AssetName) ??
		items.find((item) => assetOf(item) != null) ??
		items[0];
	return home.to;
};

const CustomNavbar = () => {
	const hasPermissionTo = useHasPermission();
	const scope = useUserScope();

	const [anchorElements, setAnchorElements] = useState<{
		[key: string]: HTMLElement | null;
	}>({});

	const handleMenuOpen = (
		event: React.MouseEvent<HTMLButtonElement>,
		menuId: string,
	) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: event.currentTarget,
		}));
	};

	const handleMenuClose = (menuId: string) => {
		setAnchorElements((prevAnchors) => ({
			...prevAnchors,
			[menuId]: null,
		}));
	};

	if (scope.isLoading) return null;

	const menus: MenuConfig[] = scope.isFull
		? menuConfigData
		: getScopedItems(scope).map((item) => ({
				id: item.to,
				label: item.scopeLabel ?? item.label,
				to: item.to,
				items: [],
				permission: item.permission,
			}));

	return (
		<>
			{menus.map((menu) => {
				if (menu.permission && !hasPermissionTo(menu.permission)) {
					return null;
				}

				const currentAnchorEl = anchorElements[menu.id] || null;
				const isMenuOpen = Boolean(currentAnchorEl);

				return menu.items.length > 0 ? (
					<React.Fragment key={menu.id}>
						<Button
							aria-controls={`${menu.id}-menu`}
							aria-haspopup="true"
							onClick={(event) => handleMenuOpen(event, menu.id)}
							sx={{
								marginRight: (theme) => theme.spacing(2),
								color: "white",
								display: "flex",
								alignItems: "center",
							}}
						>
							{menu.label}
							{isMenuOpen ? (
								<ExpandLess
									sx={{
										marginLeft: (theme) => theme.spacing(0.5),
									}}
								/>
							) : (
								<ExpandMore
									sx={{
										marginLeft: (theme) => theme.spacing(0.5),
									}}
								/>
							)}
						</Button>
						<Menu
							id={`${menu.id}-menu`}
							anchorEl={currentAnchorEl}
							keepMounted
							open={isMenuOpen}
							onClose={() => handleMenuClose(menu.id)}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "center",
							}}
						>
							{menu.items
								.filter(
									(item) =>
										!item.permission || hasPermissionTo(item.permission),
								)
								.map((item, index) => (
									<MenuItem
										key={`${menu.id}-item-${index}`}
										component={Link}
										to={item.to}
										onClick={() => handleMenuClose(menu.id)}
										sx={{
											textDecoration: "none",
											color: "inherit",
										}}
									>
										<Typography>{item.label}</Typography>
									</MenuItem>
								))}
						</Menu>
					</React.Fragment>
				) : (
					<Button
						key={menu.id}
						component={Link}
						to={menu.to || "/"}
						sx={{
							marginRight: (theme) => theme.spacing(2),
							color: "white",
							display: "flex",
							alignItems: "center",
						}}
					>
						{menu.label}
					</Button>
				);
			})}
		</>
	);
};

export { CustomNavbar };
